import { useState, useEffect, useMemo, useRef } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useFilters } from '../../hooks/useFilters';
import { FiltroAutocomplete, FiltroCatalogoFijo, FiltroRangoFechas, FiltroRangoNumerico, FiltroUnicoAutocomplete, FiltroUnicoCatalogoFijo } from '../../components/ui/FiltrosGlobales';
import { useCatalogos } from '../../context/CatalogosContext';
import { MonitoreoService } from '../../service/monitoreo/MonitoreoService';
import { useToken } from '../../components/useToken';
import { Generico } from '../../components/Generico';
import { Skeleton } from 'primereact/skeleton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { KPIChart } from '../../models/charts/KPIChart';
import { PieChart } from '../../models/charts/PieChart';
import { LineChart } from '../../models/charts/LineChart';
import { ApacheEchart } from '../../components/ApacheEchart';
import { useLazyParams } from '../../hooks/useLazyParams';
import { Dialog } from 'primereact/dialog';
import { TablaDinamica } from '../../components/TablaDinamica';
import { DetalleKpiDashboard } from '../../models/matriz/DetalleKpiDashboard';
import { Tag } from 'primereact/tag';
import { getMatrizClienteDashboardTemplate } from '../templates/pdf/MatrizClienteDashboardTemplate';
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Toast } from 'primereact/toast';
import { SelectButton } from 'primereact/selectbutton';
import { motion, AnimatePresence } from 'framer-motion';
pdfMake.addVirtualFileSystem(pdfFonts);

const monitoreoService = new MonitoreoService();

const URL_CATALOGOS_MODULO = 'dashboard/catalogos/iniciar-catalogos/matriz';

const CATALOGOS = [
    'NIVELES_RIESGO',
    'CANAL_SERVICIO_BIEN',
    'INSTRUMENTO_MONETARIO',
    'OTRAS_SENIALES',
    'TIPO_BIEN',
    'TIPO_ESTRUCTURA_LEGAL',
    'TIPO_TRANSACCION',
    'PROCEDIMIENTO_CONTROL',
    'PROCEDIMIENTO_APLICADO',
];

export const MatrizDashboard = () => {
    const { iniciarCatalogos } = useCatalogos();
    const [catalogos, setCatalogos] = useState({});
    const { getRol } = useToken();
    const toast = useRef(null);
    const rol = getRol(); // Cambia en cada render, no usarlo como dependencia de un useEffect o extraerlo fuera del componente
    const [esAdmin, setEsAdmin] = useState(false);
    // Estados para Empresa
    const [empresasUsuario, setEmpresasUsuario] = useState([]);
    const [empresaSel, setEmpresaSel] = useState(null);
    const [areasEmpresa, setAreasEmpresa] = useState([]);
    const [areaSel, setAreaSel] = useState(null);
    // Controla si los catálogos ya se descargaron del back
    const [catalogosListos, setCatalogosListos] = useState(false);
    const contextoListo = Boolean(empresaSel?.value && areaSel?.value);
    // Control de datos históricos
    const [vistaHistorica, setVistaHistorica] = useState(false);
    // Control de fechas y reporte
    const { fechasDiferenciaEnDias, funcSistemaError, funcMessageBlobError, downloadPdf, limpiarNombre } = Generico();
    const hoy = new Date();
    const primerDiaAnio = new Date(hoy.getFullYear(), 0, 1);
    const [rangoMaxDias, setRangoMaxDias] = useState(365);
    const [filtrosVisibles, setFiltrosVisibles] = useState(false);
    // Detalles en los kpi
    const [kpiModal, setKpiModal] = useState({ visible: false, tipo: null, title: '' });
    const DetalleKpi = useMemo(() => new DetalleKpiDashboard(), []);
    const estadoTabla = useLazyParams({ modelo: DetalleKpi.model, initialSortField: 'fecha_evaluacion', initialSortOrder: -1, initialRows: 5 });
    // Manejo dinámico de filtros
    const containerRef = useRef(null);
    const [maxFiltrosTop, setMaxFiltrosTop] = useState(4);

    // Impresion Pdf
    const [loadingPdf, setLoadingPdf] = useState(false);
    const chartsRefs = useRef({});
    const setChartRef = (key) => (element) => {
        if (element) {
            chartsRefs.current[key] = element;
        } else {
            delete chartsRefs.current[key];
        }
    };

    const capturarTodosLosGraficos = () => {
        const capturas = {};
        // Diccionario de los tamaños exactos definidos en el DIMS del PDF
        const DIMENSIONES_PDF = {
            'aceptados-0': { targetWidth: 266, targetHeight: 95 },
            'aceptados-1': { targetWidth: 266, targetHeight: 95 },
            'aceptados-2': { targetWidth: 266, targetHeight: 95 },
            'aceptados-3': { targetWidth: 266, targetHeight: 95 },
            'alertas-0': { targetWidth: 266, targetHeight: 95 },
            'alertas-1': { targetWidth: 266, targetHeight: 95 },
            'alertas-2': { targetWidth: 266, targetHeight: 95 },
            'kpi-global-inherente': { targetWidth: 190, targetHeight: 90 },
            'pie-inherente': { targetWidth: 330, targetHeight: 290 }, // En el pdf es 310 270
            'kpi-global-residual': { targetWidth: 190, targetHeight: 90 },
            'pie-residual': { targetWidth: 310, targetHeight: 270 }, // En el pdf es 310 270
            'line-evolucion': { targetWidth: 1110, targetHeight: 450 }
        };

        Object.keys(chartsRefs.current).forEach((key) => {
            const chartComponent = chartsRefs.current[key];
            if (chartComponent && typeof chartComponent.captureImage === 'function') {
                try {
                    // Extraemos las medidas objetivo si existen para esta key
                    const sizes = DIMENSIONES_PDF[key] || {};

                    const imgBase64 = chartComponent.captureImage({
                        backgroundColor: '#ffffff',
                        ...sizes // Pasamos el targetWidth y targetHeight
                    });

                    if (imgBase64) {
                        capturas[key] = imgBase64;
                    }
                } catch (error) {
                    // console.log(error);
                }
            }
        });
        return capturas;
    };

    // Contexto Inicial
    useEffect(() => {
        monitoreoService.consultarInfo('dashboard/contexto/inicial', { rol }).then((res) => {
            const data = res.data;
            setEsAdmin(data.esAdmin);
            if (!data.esAdmin) {
                setEmpresasUsuario(data.empresas);
                if (data.empresas.length === 1) {
                    setEmpresaSel(data.empresas[0]);
                }
            }
            if (data.configuracion && data.configuracion.dias_max_ver_dashboard) {
                const maxDiasDatos = data.configuracion.dias_max_ver_dashboard;
                setRangoMaxDias(maxDiasDatos);
                const diffDays = fechasDiferenciaEnDias(primerDiaAnio, hoy);
                if (diffDays > maxDiasDatos) {
                    const nuevaFechaInicio = new Date(hoy);
                    nuevaFechaInicio.setDate(hoy.getDate() - maxDiasDatos);
                    cambiarMultiplesFiltros({
                        fecha_inicio: nuevaFechaInicio
                    });
                }
            }
        }).catch((error) => {
            funcSistemaError(error, toast);
        }).finally(() => {

        });
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (empresaSel && empresaSel.value) {
            setAreaSel(null);
            setCatalogosListos(false);
            monitoreoService.consultarInfo(`dashboard/contexto/areas/${empresaSel.value}`).then((res) => {
                const areas = res.data;
                setAreasEmpresa(areas);
                if (areas.length === 1) {
                    setAreaSel(areas[0]);
                }
            }).catch((error) => {
                funcSistemaError(error, toast);
            }).finally(() => {

            });
        } else {
            setAreasEmpresa([]);
            setAreaSel(null);
            setCatalogosListos(false);
        }
        // eslint-disable-next-line
    }, [empresaSel]);

    useEffect(() => {
        // Solo disparamos si tenemos ambos IDs
        if (empresaSel?.value && areaSel?.value) {
            setCatalogosListos(false); // Indicamos que estamos en proceso de carga
            const params = { empresa_id: empresaSel.value, area_id: areaSel.value, }
            iniciarCatalogos(URL_CATALOGOS_MODULO, CATALOGOS, params, true).then((res) => {
                setCatalogos(res.data);
                setCatalogosListos(true);
            }).catch((error) => {
                funcSistemaError(error, toast);
            }).finally(() => {

            });
        }
        // eslint-disable-next-line
    }, [empresaSel?.value, areaSel?.value]);

    // Datos del dashboard
    const fetchDashboardData = (payload) => {
        const params = {
            rol: rol,
            empresa_id: empresaSel?.value,
            area_id: areaSel?.value,
            filters: JSON.stringify(payload)
        };
        return monitoreoService.consultarInfo('dashboard/listar/matriz_cliente', params).then(res => {
            return res;
        }).catch((error) => {
            funcSistemaError(error, toast);
            return [];
        }).finally(() => {

        });
    };

    // Instanciamos el hook
    const { filtros, obtenerPayloadBackend, cambiarFiltro, cambiarMultiplesFiltros, totalFiltrosActivos, resetearFiltros, data, loading } = useFilters({
        // Principales
        cliente: { value: [], matchMode: FilterMatchMode.IN },
        identificacion: { value: [], matchMode: FilterMatchMode.IN },
        nivel_riesgo_inherente_id: { value: [], matchMode: FilterMatchMode.IN },
        nivel_riesgo_residual_id: { value: [], matchMode: FilterMatchMode.IN },
        fecha_inicio: { value: primerDiaAnio, matchMode: 'dateOnOrAfter' }, // Caso especial no existe en FilterMatchMode
        fecha_fin: { value: hoy, matchMode: 'dateOnOrBefore' }, // Caso especial no existe en FilterMatchMode
        // Cliente
        tipo_estructura_legal_id: { value: [], matchMode: FilterMatchMode.IN },
        actividad_economica_id: { value: [], matchMode: FilterMatchMode.IN },
        nacionalidad_id: { value: [], matchMode: FilterMatchMode.IN },
        pais_residencia_id: { value: [], matchMode: FilterMatchMode.IN },
        ciudad_residencia_id: { value: [], matchMode: FilterMatchMode.IN },
        // Operación
        tipo_bien_id: { value: [], matchMode: FilterMatchMode.IN },
        monto_min: { value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
        monto_max: { value: null, matchMode: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
        pais_servcio_id: { value: null, matchMode: FilterMatchMode.IN },
        ciudad_servicio_id: { value: null, matchMode: FilterMatchMode.IN },
        // Transacción
        tipo_transaccion_id: { value: [], matchMode: FilterMatchMode.IN },
        instrumento_monetario_id: { value: [], matchMode: FilterMatchMode.IN },
        canal_servicio_id: { value: [], matchMode: FilterMatchMode.IN },
        pais_fondos_id: { value: [], matchMode: FilterMatchMode.IN },
        ciudad_canal_id: { value: [], matchMode: FilterMatchMode.IN },
        // Señales de alerta
        senial_alerta_id: { value: [], matchMode: FilterMatchMode.IN },
        // Procedimientos
        procedimiento_recomendado_id: { value: [], matchMode: FilterMatchMode.IN },
        procedimiento_aplicado_id: { value: [], matchMode: FilterMatchMode.IN },
    }, fetchDashboardData, 600, catalogosListos);

    const opcionesRiesgoInherente = catalogos.NIVELES_RIESGO || [];
    const opcionesRiesgoResidual = catalogos.NIVELES_RIESGO || [];
    const opcionesTipoTransaccion = catalogos.TIPO_TRANSACCION || [];
    const opcionesTipoBien = catalogos.TIPO_BIEN || [];
    const opcionesTipoEstructura = catalogos.TIPO_ESTRUCTURA_LEGAL || [];
    const opcionesSenialAlerta = catalogos.OTRAS_SENIALES || [];
    const opcionesInstrumentoMonetario = catalogos.INSTRUMENTO_MONETARIO || [];
    const opcionesCanalServicio = catalogos.CANAL_SERVICIO_BIEN || [];
    const opcionesProcedimientoRecomendado = catalogos.PROCEDIMIENTO_CONTROL || [];
    const opcionesProcedimientoAplicado = catalogos.PROCEDIMIENTO_APLICADO || [];

    // Catalogos autocomplete
    const fetchCatalogo = (tipo) => (texto = '') => {
        const params = { texto: texto, empresa_id: empresaSel.value, area_id: areaSel.value, }
        return monitoreoService.consultarInfo(`dashboard/catalogos/autocomplete/matriz/${tipo}`, params).then((res) => {
            return res.data
        }).catch((error) => {
            funcSistemaError(error, toast);
            return [];
        }).finally(() => {

        });
    };

    const buscarSugerenciasEmpresa = (texto) => {
        return monitoreoService.consultarInfo('dashboard/contexto/empresas-buscar', { texto }).then((res) => {
            return res.data
        }).catch((error) => {
            funcSistemaError(error, toast);
            return [];
        }).finally(() => {

        });
    };

    const todosLosFiltros = [
        {
            id: 'empresa',
            label: 'Empresa *',
            show: esAdmin || !empresasUsuario || empresasUsuario.length !== 1,
            pinnedTop: true,
            componente: esAdmin ? (
                <FiltroUnicoAutocomplete id="filtro-empresa-sa" value={empresaSel} onChange={setEmpresaSel} fetchData={buscarSugerenciasEmpresa} placeholder="Buscar empresa..." />
            ) : (
                <FiltroUnicoCatalogoFijo value={empresaSel?.value} options={empresasUsuario} onChange={(val) => { const selected = empresasUsuario.find(e => e.value === val); setEmpresaSel(selected || null); }} placeholder="Seleccione Empresa" icon="pi pi-building" />
            )
        },
        {
            id: 'sector',
            label: 'Sector Económico *',
            show: !areasEmpresa || areasEmpresa.length !== 1,
            pinnedTop: true,
            componente: <FiltroUnicoCatalogoFijo value={areaSel?.value} options={areasEmpresa} placeholder={empresaSel ? "Seleccione" : "Seleccione Empresa"} onChange={(val) => { const selected = areasEmpresa.find(a => a.value === val); setAreaSel(selected || null); }} disabled={!empresaSel || areasEmpresa.length === 0} icon="pi pi-briefcase" />
        },
        {
            id: 'fechas',
            label: 'Periodo analizado',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroRangoFechas valueInicio={filtros.fecha_inicio.value} valueFin={filtros.fecha_fin.value} onChange={({ inicio, fin }) => cambiarMultiplesFiltros({ fecha_inicio: inicio, fecha_fin: fin })} />
        },
        {
            id: 'cliente',
            label: 'Nombre / Razón Social',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.cliente.value} onChange={(val) => cambiarFiltro('cliente', val)} fetchData={fetchCatalogo('CLIENTE')} tooltipPosition="left" icon="pi pi-users" />
        },
        {
            id: 'identificacion',
            label: 'Identificación',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.identificacion.value} onChange={(val) => cambiarFiltro('identificacion', val)} fetchData={fetchCatalogo('IDENTIFICACION')} icon="pi pi-id-card" />
        },
        {
            id: 'riesgo_inherente',
            label: 'Niveles de Riesgo Inherente',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.nivel_riesgo_inherente_id.value} onChange={(val) => cambiarFiltro('nivel_riesgo_inherente_id', val)} options={opcionesRiesgoInherente} icon="pi pi-exclamation-circle" />
        },
        {
            id: 'riesgo_residual',
            label: 'Niveles de Riesgo Residual',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.nivel_riesgo_residual_id.value} onChange={(val) => cambiarFiltro('nivel_riesgo_residual_id', val)} options={opcionesRiesgoResidual} icon="pi pi-info-circle" />
        },
        {
            id: 'estructura_legal',
            label: 'Estructura Legal',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.tipo_estructura_legal_id.value} onChange={(val) => cambiarFiltro('tipo_estructura_legal_id', val)} options={opcionesTipoEstructura} tooltipPosition="left" icon="pi pi-building-columns" />
        },
        {
            id: 'actividad_economica',
            label: 'Actividad Económica',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.actividad_economica_id.value} onChange={(val) => cambiarFiltro('actividad_economica_id', val)} fetchData={fetchCatalogo('ACTIVIDAD_ECONOMICA')} tooltipPosition="left" icon="pi pi-briefcase" />
        },
        {
            id: 'nacionalidad',
            label: 'Nacionalidad',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.nacionalidad_id.value} onChange={(val) => cambiarFiltro('nacionalidad_id', val)} fetchData={fetchCatalogo('NACIONALIDAD')} tooltipPosition="left" icon="pi pi-address-book" />
        },
        {
            id: 'pais_residencia',
            label: 'País de Residencia',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.pais_residencia_id.value} onChange={(val) => cambiarFiltro('pais_residencia_id', val)} fetchData={fetchCatalogo('PAIS')} tooltipPosition="left" icon="pi pi-map" />
        },
        {
            id: 'ciudad_residencia',
            label: 'Ciudad / Cantón de Residencia',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.ciudad_residencia_id.value} onChange={(val) => cambiarFiltro('ciudad_residencia_id', val)} fetchData={fetchCatalogo('CIUDAD')} tooltipPosition="left" icon="pi pi-map-marker" />
        },
        {
            id: 'tipo_bien',
            label: 'Tipo de Bien / Servicio',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.tipo_bien_id.value} onChange={(val) => cambiarFiltro('tipo_bien_id', val)} options={opcionesTipoBien} tooltipPosition="left" />
        },
        {
            id: 'monto',
            label: 'Rango de Monto',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroRangoNumerico valueMin={filtros.monto_min.value} valueMax={filtros.monto_max.value} onChange={({ min, max }) => cambiarMultiplesFiltros({ monto_min: min, monto_max: max })} />
        },
        {
            id: 'pais_servicio',
            label: 'País de Ubicación del Servicio',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.pais_servcio_id.value} onChange={(val) => cambiarFiltro('pais_servcio_id', val)} fetchData={fetchCatalogo('PAIS')} tooltipPosition="left" icon="pi pi-map" />
        },
        {
            id: 'ciudad_servicio',
            label: 'Ciudad / Cantón del Servicio',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.ciudad_servicio_id.value} onChange={(val) => cambiarFiltro('ciudad_servicio_id', val)} fetchData={fetchCatalogo('CIUDAD')} tooltipPosition="left" icon="pi pi-map-marker" />
        },
        {
            id: 'tipo_transaccion',
            label: 'Tipo de Transacción / Servicio',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.tipo_transaccion_id.value} onChange={(val) => cambiarFiltro('tipo_transaccion_id', val)} options={opcionesTipoTransaccion} tooltipPosition="left" />
        },
        {
            id: 'instrumento_monetario',
            label: 'Instrumento Monetario con que paga',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.instrumento_monetario_id.value} onChange={(val) => cambiarFiltro('instrumento_monetario_id', val)} options={opcionesInstrumentoMonetario} tooltipPosition="left" icon="pi pi-credit-card" />
        },
        {
            id: 'canal_servicio',
            label: 'Canal de Atención',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.canal_servicio_id.value} onChange={(val) => cambiarFiltro('canal_servicio_id', val)} options={opcionesCanalServicio} tooltipPosition="left" icon="pi pi-truck" />
        },
        {
            id: 'pais_fondos',
            label: 'País de Procedencia de los Fondos',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.pais_fondos_id.value} onChange={(val) => cambiarFiltro('pais_fondos_id', val)} fetchData={fetchCatalogo('PAIS')} tooltipPosition="left" icon="pi pi-map" />
        },
        {
            id: 'ciudad_canal',
            label: 'Ciudad / Cantón Donde se Ubica el Canal',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroAutocomplete value={filtros.ciudad_canal_id.value} onChange={(val) => cambiarFiltro('ciudad_canal_id', val)} fetchData={fetchCatalogo('CIUDAD')} tooltipPosition="left" icon="pi pi-map-marker" />
        },
        {
            id: 'senial_alerta',
            label: 'Señales de Alerta',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.senial_alerta_id.value} onChange={(val) => cambiarFiltro('senial_alerta_id', val)} options={opcionesSenialAlerta} tooltipPosition="left" icon="pi pi-exclamation-triangle" />
        },
        {
            id: 'procedimiento_recomendado',
            label: 'Procedimiento Recomendado',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.procedimiento_recomendado_id.value} onChange={(val) => cambiarFiltro('procedimiento_recomendado_id', val)} options={opcionesProcedimientoRecomendado} tooltipPosition="left" />
        },
        {
            id: 'procedimiento_aplicado',
            label: 'Procedimiento Aplicado',
            show: true,
            pinnedTop: false,
            componente: !contextoListo ? <Skeleton height="32.2px" /> : <FiltroCatalogoFijo value={filtros.procedimiento_aplicado_id.value} onChange={(val) => cambiarFiltro('procedimiento_aplicado_id', val)} options={opcionesProcedimientoAplicado} tooltipPosition="left" icon="pi pi-list-check" />
        }
    ];

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver((entries) => {
            const containerWidth = entries[0].contentRect.width;
            const ANCHO_BASE_FILTRO = 250; // Tamaño estimado para el calculo de cuantos caben.
            const GAP = 16;
            const cantidadCalculada = Math.floor((containerWidth + GAP) / (ANCHO_BASE_FILTRO + GAP));
            setMaxFiltrosTop(cantidadCalculada > 0 ? cantidadCalculada : 1);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // DIVIDIR LOS FILTROS ACTIVOS (Los primeros X van arriba, el resto al Sidebar)
    const filtrosActivos = todosLosFiltros.filter(f => f.show);
    const filtrosTop = [];
    const filtrosSidebar = [];
    let contadorTop = 0;
    filtrosActivos.forEach(filtro => {
        if (filtro.pinnedTop || contadorTop < maxFiltrosTop) {
            filtrosTop.push(filtro);
            contadorTop++;
        } else {
            filtrosSidebar.push(filtro);
        }
    });

    const abrirDetalleKpi = (tipo, title) => {
        setKpiModal({ visible: true, tipo, title });
    };

    useEffect(() => {
        if (!kpiModal.visible || !kpiModal.tipo) return;
        estadoTabla.setLoading(true);
        const payload = {
            ...estadoTabla.lazyParams,
            rol: rol,
            dashboard_filters: obtenerPayloadBackend(), // Viaja con su propia clave para no chocar con los filtros de lazyParams
            kpi_tipo: kpiModal.tipo,
            empresa_id: empresaSel?.value,
            area_id: areaSel?.value,
            vista_historica: vistaHistorica,
        };

        monitoreoService.consultarInfo('dashboard/detalle/matriz_cliente/detalle-kpi', payload).then(res => {
            estadoTabla.setData(res.data.registros);
            estadoTabla.setTotalRecords(res.data.total);
        }).catch(error => {
            funcSistemaError(error, toast);
        }).finally(() => {
            estadoTabla.setLoading(false)
        });
        // eslint-disable-next-line
    }, [estadoTabla.lazyParams, kpiModal.visible, kpiModal.type]);

    // Helper para calcular la tendencia (%) entre actual e histórico para el KPIChart
    const calcularTendencia = (actual = 0, historico = 0) => {
        if (historico === 0) return actual > 0 ? 100 : 0;
        return Math.round(((actual - historico) / historico) * 100);
    };

    const kpis = useMemo(() => data?.data?.kpis || {}, [data]);
    const graficos = useMemo(() => data?.data?.graficos || {}, [data]);

    const coloresAceptados = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
    const coloresAlertas = ['#3b82f6', '#ef4444', '#f97316'];

    const kpiAceptados = useMemo(() => [
        {
            tipo: 'totales',
            title: vistaHistorica ? 'Total Matrices' : 'Clientes Únicos',
            options: new KPIChart({
                value: vistaHistorica ? (kpis.total_matrices_historicas || 0) : (kpis.total_clientes_unicos || 0),
                kpiTitle: vistaHistorica ? 'Matrices' : 'Clientes',
                themeColor: '#3b82f6',
                valueColor: '#3b82f6',
                icon: 'pi pi-users',
                layout: 'modern',
                bottomRightInfo: { text: vistaHistorica ? `Clientes: ${kpis.total_clientes_unicos || 0}` : `Matrices: ${kpis.total_matrices_historicas || 0}`, icon: 'pi pi-database' }
            }).getOptions()
        },
        {
            tipo: 'aceptados',
            title: 'Aceptados',
            options: new KPIChart({
                value: vistaHistorica ? (kpis.aceptados_historico || 0) : (kpis.aceptados_actual || 0),
                kpiTitle: 'Aceptados',
                themeColor: '#10b981',
                valueColor: '#10b981',
                icon: 'pi pi-check-circle',
                layout: 'modern',
                showTrend: !vistaHistorica,
                trendValue: calcularTendencia(kpis.aceptados_actual, kpis.aceptados_historico),
                bottomRightInfo: { text: vistaHistorica ? `Actual: ${(kpis.aceptados_actual || 0)}` : `Histórico: ${(kpis.aceptados_historico || 0)}` },
            }).getOptions()
        },
        {
            tipo: 'seguimiento',
            title: 'En Seguimiento Activo',
            options: new KPIChart({
                value: vistaHistorica ? (kpis.en_seguimiento_historico || 0) : (kpis.en_seguimiento_actual || 0),
                kpiTitle: 'En Seguimiento Activo',
                themeColor: '#f59e0b',
                valueColor: '#f59e0b',
                icon: 'pi pi-eye',
                layout: 'modern',
                showTrend: !vistaHistorica,
                trendType: 'positive_down',
                trendValue: calcularTendencia(kpis.en_seguimiento_actual, kpis.en_seguimiento_historico),
                bottomRightInfo: { text: vistaHistorica ? `Actual: ${kpis.en_seguimiento_actual || 0}` : `Histórico: ${kpis.en_seguimiento_historico || 0}` }
            }).getOptions()
        },
        {
            tipo: 'no_aceptados',
            title: 'No Aceptados',
            options: new KPIChart({
                value: vistaHistorica ? (kpis.no_aceptados_historico || 0) : (kpis.no_aceptados_actual || 0),
                kpiTitle: 'No Aceptados',
                themeColor: '#ef4444',
                valueColor: '#ef4444',
                icon: 'pi pi-times-circle',
                layout: 'modern',
                showTrend: !vistaHistorica,
                trendType: 'positive_down',
                trendValue: calcularTendencia(kpis.no_aceptados_actual, kpis.no_aceptados_historico),
                bottomRightInfo: { text: vistaHistorica ? `Actual: ${kpis.no_aceptados_actual || 0}` : `Histórico: ${kpis.no_aceptados_historico || 0}` }
            }).getOptions()
        }
    ], [kpis, vistaHistorica]);

    const kpiAlertas = useMemo(() => [
        {
            tipo: 'sin_alerta',
            title: 'Sin Alertas',
            options: new KPIChart({
                value: vistaHistorica ? (kpis.sin_alerta_historico || 0) : (kpis.sin_alerta_actual || 0),
                kpiTitle: 'Sin Alertas',
                themeColor: '#3b82f6',
                valueColor: '#3b82f6',
                icon: 'pi pi-shield',
                layout: 'modern',
                showTrend: !vistaHistorica,
                trendType: 'positive_up',
                trendValue: calcularTendencia(kpis.sin_alerta_actual, kpis.sin_alerta_historico),
                bottomRightInfo: { text: vistaHistorica ? `Actual: ${kpis.sin_alerta_actual || 0}` : `Histórico: ${kpis.sin_alerta_historico || 0}` }
            }).getOptions()
        },
        {
            tipo: 'pep',
            title: "PEP's",
            options: new KPIChart({
                value: vistaHistorica ? (kpis.pep_historico || 0) : (kpis.pep_actual || 0),
                kpiTitle: "PEP's",
                themeColor: '#ef4444',
                valueColor: '#ef4444',
                icon: 'pi pi-exclamation-triangle',
                layout: 'modern',
                showTrend: !vistaHistorica,
                trendType: 'neutral',
                trendValue: calcularTendencia(kpis.pep_actual, kpis.pep_historico),
                bottomRightInfo: { text: vistaHistorica ? `Actual: ${kpis.pep_actual || 0}` : `Histórico: ${kpis.pep_historico || 0}` }
            }).getOptions()
        },
        {
            tipo: 'otras_alertas',
            title: 'Con Señales de Alerta',
            options: new KPIChart({
                value: vistaHistorica ? (kpis.otras_alertas_historico || 0) : (kpis.otras_alertas_actual || 0),
                kpiTitle: 'Con Señales de Alerta',
                themeColor: '#f97316',
                valueColor: '#f97316',
                icon: 'pi pi-bell',
                layout: 'modern',
                showTrend: !vistaHistorica,
                trendType: 'positive_down',
                trendValue: calcularTendencia(kpis.otras_alertas_actual, kpis.otras_alertas_historico),
                bottomRightInfo: { text: vistaHistorica ? `Actual: ${kpis.otras_alertas_historico || 0}` : `Histórico: ${kpis.otras_alertas_historico || 0}` }
            }).getOptions()
        }
    ], [kpis, vistaHistorica]);

    const mapearParaTabla = (distribucionObj) => {
        if (!distribucionObj) return [];
        return Object.entries(distribucionObj).map(([nivel, cantidad]) => ({
            nivel,
            cantidad,
        }));
    };

    const mapearParaPieChart = (distribucionObj, catalogoConColor) => {
        if (!distribucionObj || !catalogoConColor) return [];
        return Object.entries(distribucionObj).filter(([, cantidad]) => cantidad > 0).map(([nivel, cantidad]) => {
            const coincidencia = catalogoConColor.find(
                cat => cat.label.toUpperCase() === nivel.toUpperCase()
            );
            const colorHex = coincidencia?.color ? `#${coincidencia.color}` : '#cccccc';
            return {
                name: nivel,
                value: cantidad,
                itemStyle: { color: colorHex }, // Necesario para aplicar correctamente el color
            };
        });
    };

    const estiloCalificacion = (riesgo) => {
        if (riesgo === null || riesgo === undefined) return { bg: '#e2e8f0', text: '#64748b', label: '' };
        const valorNum = parseFloat(riesgo);
        const niveles = catalogos?.NIVELES_RIESGO || [];
        const coincidencia = niveles.find(cat => {
            const min = parseFloat(cat.minimo);
            const max = parseFloat(cat.maximo);
            if (valorNum === 0 && min === 0) {
                return true; // Caso especial
            }
            return valorNum > min && valorNum <= max;
        });
        if (coincidencia) {
            return {
                bg: coincidencia.color?.startsWith('#') ? coincidencia.color : `#${coincidencia.color}`,
                text: coincidencia.color_texto?.startsWith('#') ? coincidencia.color_texto : `#${coincidencia.color_texto}`,
                label: coincidencia.label
            };
        }
        // Fallback azul y sin texto: error en la información
        return { bg: '#3b82f6', text: '#ffffff', label: '' };
    };

    const tablaInherente = useMemo(() => mapearParaTabla(vistaHistorica ? graficos.distribucion_inherente_historica : graficos.distribucion_inherente_actual), [graficos, vistaHistorica]);
    const tablaResidual = useMemo(() => mapearParaTabla(vistaHistorica ? graficos.distribucion_residual_historica : graficos.distribucion_residual_actual), [graficos, vistaHistorica]);

    const pieDataInherente = useMemo(() => mapearParaPieChart(vistaHistorica ? graficos.distribucion_inherente_historica : graficos.distribucion_inherente_actual, catalogos.NIVELES_RIESGO || []), [graficos, vistaHistorica, catalogos.NIVELES_RIESGO]);
    const pieDataResidual = useMemo(() => mapearParaPieChart(vistaHistorica ? graficos.distribucion_residual_historica : graficos.distribucion_residual_actual, catalogos.NIVELES_RIESGO || []), [graficos, vistaHistorica, catalogos.NIVELES_RIESGO]);

    const pieChartInherente = useMemo(() => {
        const chart = new PieChart({
            data: pieDataInherente,
            showLabels: true,
            labelPosition: 'outside',
            showPercentage: true,
            legendOrient: 'horizontal',
            legendTop: 'top',
            legendLeft: 'center',
        });
        return chart
            .transformToDonut('45%', '75%')
            .getOptions();
    }, [pieDataInherente]);

    const pieChartResidual = useMemo(() => {
        const chart = new PieChart({
            data: pieDataResidual,
            showLabels: true,
            labelPosition: 'outside',
            showPercentage: true,
            legendOrient: 'horizontal',
            legendTop: 'top',
            legendLeft: 'center',
        });
        return chart
            .transformToDonut('45%', '75%')
            .getOptions();
    }, [pieDataResidual]);

    const kpiGlobalInherente = useMemo(() => {
        const estilo = estiloCalificacion(kpis.promedio_riesgo_inherente_actual);
        const promedioInherenteActual = kpis.promedio_riesgo_inherente_actual || 0;
        const promedioInherenteHistorico = kpis.promedio_riesgo_inherente_historico || 0;
        const chart = new KPIChart({
            value: vistaHistorica ? promedioInherenteHistorico : promedioInherenteActual,
            kpiTitle: '',
            layout: 'simple',
            valueFontSize: 24,
            themeColor: estilo.bg,
            showTrend: !vistaHistorica,
            trendType: 'neutral',
            trendValue: calcularTendencia(parseFloat(promedioInherenteActual), parseFloat(promedioInherenteHistorico))
        });
        return chart.getOptions();
        // eslint-disable-next-line
    }, [kpis, vistaHistorica]);

    const kpiGlobalResidual = useMemo(() => {
        const estilo = estiloCalificacion(kpis.promedio_riesgo_residual_actual);
        const promedioResidualActual = kpis.promedio_riesgo_residual_actual || 0;
        const promedioResidualHistorico = kpis.promedio_riesgo_residual_historico || 0;
        const chart = new KPIChart({
            value: vistaHistorica ? promedioResidualHistorico : promedioResidualActual,
            kpiTitle: '',
            layout: 'simple',
            valueFontSize: 24,
            themeColor: estilo.bg,
            showTrend: !vistaHistorica,
            trendType: 'neutral',
            trendValue: calcularTendencia(parseFloat(promedioResidualActual), parseFloat(promedioResidualHistorico))
        });
        return chart.getOptions();
        // eslint-disable-next-line
    }, [kpis, vistaHistorica]);

    const lineChartEvolucion = useMemo(() => {
        const timeData = graficos.linea_tiempo || [];
        return new LineChart({
            data: timeData,
            xKey: 'periodo',
            multipleSeries: true,
            smooth: true,
            areaStyle: true,
            showSymbols: true,
            symbolSize: 8,
            xAxisName: 'Fechas de Análisis',
            yAxisName: 'Nivel de Riesgo',
            legendTop: 'top',
            seriesList: [
                {
                    name: 'Riesgo Inherente',
                    // Mapeamos el dato asegurando que sea numérico
                    data: timeData.map(item => ({ value: parseFloat(item.promedio_inherente || 0) })),
                    color: '#f97316'
                },
                {
                    name: 'Riesgo Residual',
                    data: timeData.map(item => ({ value: parseFloat(item.promedio_residual || 0) })),
                    color: '#3b82f6'
                }
            ]
        }).getOptions();
    }, [graficos]);

    const bodyTemplates = useMemo(() => ({
        // PrimeReact nos pasa (rowData, options) en los bodys de las Column
        riesgoTemplate: (rowData, options) => {
            // TablaDinamica guardara field como la key de la columna field="calificacion_riesgo_inherente"
            const calificacion = rowData[options.field];
            const estilo = estiloCalificacion(calificacion);
            return (
                <div className="flex justify-content-center">
                    <Tag style={{ backgroundColor: estilo.bg, color: estilo.text }} value={calificacion ? `${calificacion} ${estilo.label ? `(${estilo.label})` : ''}` : '-'} className="px-3 py-1 shadow-1" />
                </div>
            );
        }
        // eslint-disable-next-line
    }), [catalogos]);

    const obtenerFiltrosLegibles = () => {
        const filtrosActivos = [];

        const catalogosMap = {
            nivel_riesgo_inherente_id: opcionesRiesgoInherente,
            nivel_riesgo_residual_id: opcionesRiesgoResidual,
            tipo_estructura_legal_id: opcionesTipoEstructura,
            tipo_bien_id: opcionesTipoBien,
            tipo_transaccion_id: opcionesTipoTransaccion,
            instrumento_monetario_id: opcionesInstrumentoMonetario,
            canal_servicio_id: opcionesCanalServicio,
            senial_alerta_id: opcionesSenialAlerta,
            procedimiento_recomendado_id: opcionesProcedimientoRecomendado,
            procedimiento_aplicado_id: opcionesProcedimientoAplicado
        };

        const diccionarioTitulos = {
            cliente: 'Cliente / Razón Social',
            identificacion: 'Identificación',
            nivel_riesgo_inherente_id: 'Riesgo Inherente',
            nivel_riesgo_residual_id: 'Riesgo Residual',
            tipo_estructura_legal_id: 'Estructura Legal',
            actividad_economica_id: 'Actividad Económica',
            nacionalidad_id: 'Nacionalidad',
            pais_residencia_id: 'País de Residencia',
            ciudad_residencia_id: 'Ciudad de Residencia',
            tipo_bien_id: 'Tipo de Bien/Servicio',
            pais_servcio_id: 'País Servicio',
            ciudad_servicio_id: 'Ciudad Servicio',
            tipo_transaccion_id: 'Servicio / Transacción',
            instrumento_monetario_id: 'Instrumento Monetario',
            canal_servicio_id: 'Canal de Atención',
            pais_fondos_id: 'País Origen Fondos',
            ciudad_canal_id: 'Ciudad Canal',
            senial_alerta_id: 'Señales de Alerta',
            procedimiento_recomendado_id: 'Procedimiento Recomendado',
            procedimiento_aplicado_id: 'Procedimiento Aplicado'
        };

        const mismodia = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

        const extraerTexto = (val, key) => {
            if (Array.isArray(val)) {
                return val.map(v => {
                    if (v !== null && typeof v === 'object') return v.label || v.name || v.value;
                    if (catalogosMap[key]) {
                        const encontrado = catalogosMap[key].find(opt => String(opt.value) === String(v));
                        return encontrado ? (encontrado.label || encontrado.name) : v;
                    }
                    return v;
                });
            }
            if (val && typeof val === 'object') {
                if (val instanceof Date) return val.toLocaleDateString();
                return val.label || val.name || val.value || '';
            }
            return val;
        };

        const valInicio = filtros.fecha_inicio.value;
        const valFin = filtros.fecha_fin.value;

        const inicioCambio = valInicio instanceof Date && !mismodia(valInicio, primerDiaAnio);
        const finCambio = valFin instanceof Date && !mismodia(valFin, hoy);

        const fIn = valInicio ? extraerTexto(valInicio, 'fecha_inicio') : '---';
        const fFin = valFin ? extraerTexto(valFin, 'fecha_fin') : '---';

        filtrosActivos.push({ label: 'Fecha de Inicio', value: fIn, isDefault: !inicioCambio });
        filtrosActivos.push({ label: 'Fecha de Fin', value: fFin, isDefault: !finCambio });

        // FILTROS DINÁMICOS
        Object.keys(diccionarioTitulos).forEach(key => {
            const filterObj = filtros[key];
            if (filterObj && filterObj.value) {
                const val = filterObj.value;
                const esArregloVacio = Array.isArray(val) && val.length === 0;
                if (!esArregloVacio && val !== null && val !== '') {
                    filtrosActivos.push({ label: diccionarioTitulos[key], value: extraerTexto(val, key), isDefault: false });
                }
            }
        });

        // MONTOS
        if (filtros.monto_min.value !== null || filtros.monto_max.value !== null) {
            const min = filtros.monto_min.value !== null ? filtros.monto_min.value : '0';
            const max = filtros.monto_max.value !== null ? filtros.monto_max.value : 'Sin límite';

            filtrosActivos.push({ label: 'Monto Mínimo', value: `$${min}`, isDefault: filtros.monto_min.value === null });
            filtrosActivos.push({ label: 'Monto Máximo', value: `$${max}`, isDefault: filtros.monto_max.value === null });
        }

        return filtrosActivos;
    };

    const exportarPDF = () => {
        setLoadingPdf(true);
        monitoreoService.consultarInfo('dashboard/reporte/monitoreo').then(res => {
            const nombreEmpresa = empresaSel ? empresaSel.label : '';
            const nombreSector = areaSel ? areaSel.label : '';
            const filtrosAplicados = obtenerFiltrosLegibles();
            const info = {
                empresa: res.data.razon_social,
                logo: res.data.logo,
                nombre_usuario: res.data.nombre_usuario,
                fechaGeneracion: res.data.fecha_generacion,
                empresaSeleccionada: nombreEmpresa,
                sectorSeleccionado: nombreSector,
                filtrosAplicados: filtrosAplicados,
                totalFiltros: totalFiltrosActivos,
                vistaHistorica: vistaHistorica,
            };
            const images = capturarTodosLosGraficos();
            const tablas = {
                inherente: tablaInherente,
                residual: tablaResidual
            };
            const docDefinition = getMatrizClienteDashboardTemplate(images, tablas, info);
            pdfMake.createPdf(docDefinition).download(`Dashboard_Riesgos_${new Date().toISOString().split('T')[0]}.pdf`);
        }).catch((error) => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Ocurrió un error al cargar la información para el reporte.', life: 3000 });
        }).finally(() => {
            setLoadingPdf(false);
        });
    };

    const generarReporte = (rowData) => {
        estadoTabla.setLoading(true);
        const nombreArchivo = `Matriz Cliente - ${limpiarNombre(rowData.nombre_cliente)}.pdf`;
        monitoreoService.descargar('matriz/cliente/reporte', rowData.id).then(res => {
            downloadPdf(res, nombreArchivo);
        }).catch(error => {
            funcMessageBlobError('A ocurrido un error.', error, toast);
        }).finally(() => {
            estadoTabla.setLoading(false);
        });
    };

    const accionBodyTemplate = (rowData) => {
        return (
            <Button icon="pi pi-file-pdf" rounded severity="danger" aria-label="Descargar Reporte" tooltip="Descargar Matriz Cliente" tooltipOptions={{ position: 'left' }} onClick={() => generarReporte(rowData)} />
        );
    };

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionBodyTemplate} style={{ minWidth: '80px', textAlign: 'center' }} alignFrozen="right" frozen />
    );

    return (
        <>
            <Toast ref={toast} />
            <div className="card p-3">
                {/* CABECERA Y FILTROS INTEGRADOS */}
                <div className="flex align-items-center justify-content-between mb-2 border-bottom-1 border-300 pb-2">
                    <div className="flex align-items-center gap-4">
                        <h5 className="m-0 text-700 flex align-items-center">
                            <i className="pi pi-filter mr-2 text-primary"></i>Parámetros y Filtros
                        </h5>

                        {!contextoListo ? (
                            <Skeleton width="210px" height="33px" />
                        ) : (
                            <SelectButton value={vistaHistorica}
                                options={[
                                    { label: 'Histórico', value: true, icon: 'pi pi-history' },
                                    { label: 'Última Matriz', value: false, icon: 'pi pi-check-circle' }
                                ]}
                                itemTemplate={(option) => (
                                    <div className="flex align-items-center gap-2">
                                        <i className={option.icon} style={{ fontSize: '0.875rem' }}></i>
                                        <span className="text-sm font-semibold">{option.label}</span>
                                    </div>
                                )}
                                onChange={(e) => {
                                    // Evita que el usuario deseleccione y se quede en null
                                    if (e.value !== null) { setVistaHistorica(e.value); }
                                }}
                                pt={{ button: { className: 'py-2 px-3' } }}
                            />
                        )}
                    </div>
                    <div className="flex gap-2">
                        {!contextoListo ? (
                            <Skeleton width="100px" height="33px" />
                        ) : (
                            <>
                                {totalFiltrosActivos > 0 && (
                                    <Button label="Limpiar Filtros" icon="pi pi-filter-slash" severity="secondary" size="small" badge={totalFiltrosActivos} badgeClassName="p-badge-secondary" onClick={resetearFiltros} text />
                                )}
                                <Button type="button" label="Más Filtros" icon="pi pi-sliders-h" size="small" onClick={() => setFiltrosVisibles(true)} />
                            </>
                        )}
                    </div>
                </div>

                {/* GRID DE FILTROS COMPACTO */}
                <div ref={containerRef} className="flex flex-wrap gap-3 mb-3 bg-white border-round p-fluid w-full">
                    <AnimatePresence mode="popLayout">
                        {filtrosTop.map((filtro) => (
                            <motion.div key={filtro.id} layout="position" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0, flex: "1 1 250px" }} exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.2 }} className="py-2"
                                style={{ maxWidth: '500px', minWidth: '200px', boxSizing: 'border-box' }}>
                                <label className="text-xs font-bold text-primary mb-1 block">
                                    {filtro.label}
                                </label>
                                {filtro.componente}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>

                {/* SIDEBAR PARA LOS FILTROS SOBRANTES */}
                <Sidebar visible={filtrosVisibles} position="right" onHide={() => setFiltrosVisibles(false)} style={{ width: '350px' }}>
                    <h3 className="mt-0 mb-2">Filtros Avanzados</h3>
                    <p className="text-500 text-sm mb-4 border-bottom-1 border-300 pb-3">Refine su búsqueda utilizando los siguientes parámetros.</p>

                    <div className="flex flex-column gap-3 p-fluid">
                        {filtrosSidebar.length > 0 ? (
                            filtrosSidebar.map((filtro) => (
                                <div key={filtro.id} className="fadein animation-duration-400">
                                    <label className="text-sm text-600 block mb-1">{filtro.label}</label>
                                    {filtro.componente}
                                </div>
                            ))
                        ) : (
                            <p className="text-500 text-sm italic">Todos los filtros están visibles en la barra superior.</p>
                        )}
                        <div className="pb-5"></div>
                    </div>
                </Sidebar>

                {/* CUERPO DEL DASHBOARD */}
                <div className="flex flex-column gap-3 mt-2 fadein animation-duration-500">
                    {/* FILA 1: Segmentación de clientes aceptados (4 KPIs) */}
                    <div className="surface-ground p-2 border-round">
                        <h6 className="flex align-items-center gap-2 mt-0 mb-2 text-700 text-sm uppercase">
                            <i className="pi pi-users text-green-600"></i> Segmentación de clientes aceptados
                        </h6>
                        <div className="grid">
                            {(!contextoListo ? Array.from({ length: 4 }) : kpiAceptados).map((item, i) => (
                                <div key={`aceptados-${i}`} className="col-12 md:col-3">
                                    <div className="bg-white p-2 border-round shadow-1 border-left-2 flex flex-column kpi-dashboard-hover"
                                        style={{ height: '90px', borderLeftColor: coloresAceptados[i] }}
                                        onClick={() => contextoListo && item && abrirDetalleKpi(item.tipo, item.title)}>
                                        {!contextoListo ? <Skeleton width="100%" height="100%" /> : (
                                            <div style={{ flex: 1, position: 'relative', minHeight: 0, minWidth: 0 }}>
                                                {/* Pasamos item.options porque cambiamos la estructura */}
                                                <ApacheEchart chartOpciones={item.options} isLoading={loading} ref={setChartRef(`aceptados-${i}`)} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* FILA 2: Segmentación por Alertas (3 KPIs) */}
                    <div className="surface-ground p-2 border-round">
                        <h6 className="flex align-items-center gap-2 mt-0 mb-2 text-700 text-sm uppercase">
                            <i className="pi pi-bell text-red-600"></i> Segmentación de clientes por Alertas
                        </h6>
                        <div className="grid justify-content-center">
                            {(!contextoListo ? Array.from({ length: 3 }) : kpiAlertas).map((item, i) => (
                                <div key={`alertas-${i}`} className="col-12 md:col-3">
                                    <div className="bg-white p-2 border-round shadow-1 border-left-2 flex flex-column cursor-pointer kpi-dashboard-hover"
                                        style={{ height: '90px', borderLeftColor: coloresAlertas[i] }}
                                        onClick={() => contextoListo && item && abrirDetalleKpi(item.tipo, item.title)}>
                                        {!contextoListo ? <Skeleton width="100%" height="100%" /> : (
                                            <div style={{ flex: 1, position: 'relative', minHeight: 0, minWidth: 0 }}>
                                                <ApacheEchart chartOpciones={item.options} isLoading={loading} ref={setChartRef(`alertas-${i}`)} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* FILA 3: Clientes por Nivel de Riesgo */}
                    <div className="surface-ground p-2 border-round">
                        <h6 className="flex align-items-center gap-2 mt-0 mb-2 text-700 text-sm uppercase">
                            <i className="pi pi-shield text-orange-600"></i> Clientes por Nivel de Riesgo
                        </h6>
                        <div className="grid">
                            {/* PANEL IZQUIERDO: RIESGO INHERENTE */}
                            <div className="col-12 xl:col-6">
                                <div className="bg-white p-3 border-round shadow-1 h-full">
                                    <h6 className="text-center mt-0 mb-3 text-600 font-bold uppercase text-xs">Riesgo Inherente</h6>
                                    <div className="grid align-items-center">
                                        {/* Lado Izquierdo (1/3): KPI Arriba, Tabla Abajo */}
                                        <div className="col-12 md:col-4 flex flex-column gap-3">
                                            {/* KPI Global */}
                                            <div className="bg-gray-50 p-2 border-round border-1 border-300 flex flex-column align-items-center justify-content-center" style={{ height: '80px' }}>
                                                <span className="text-500 font-bold text-xs mb-1 text-center">{vistaHistorica ? 'Riesgo Global Inherente (Histórico)' : 'Riesgo Global Inherente'}</span>
                                                {!contextoListo ? <Skeleton width="80%" height="40px" /> : (
                                                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                                        <ApacheEchart chartOpciones={kpiGlobalInherente} isLoading={loading} ref={setChartRef('kpi-global-inherente')} />
                                                    </div>
                                                )}
                                            </div>
                                            {/* Tabla */}
                                            {!contextoListo ? <Skeleton width="100%" height="140px" /> : (
                                                <DataTable value={tablaInherente || []} loading={loading} size="small" stripedRows className="border-1 border-300 border-round text-xs">
                                                    <Column field="nivel" header="Nivel" bodyClassName="font-bold"></Column>
                                                    <Column field="cantidad" header={vistaHistorica ? "No. Matrices" : "No. Clientes"} align="right"></Column>
                                                </DataTable>
                                            )}
                                        </div>
                                        {/* Lado Derecho (2/3): Pie Chart */}
                                        <div className="col-12 md:col-8">
                                            {!contextoListo ? <Skeleton shape="circle" size="240px" className="mx-auto" /> : (
                                                <div style={{ height: '260px', width: '100%', position: 'relative' }}>
                                                    <ApacheEchart chartOpciones={pieChartInherente} isLoading={loading} ref={setChartRef('pie-inherente')} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* PANEL DERECHO: RIESGO RESIDUAL */}
                            <div className="col-12 xl:col-6">
                                <div className="bg-white p-3 border-round shadow-1 h-full">
                                    <h6 className="text-center mt-0 mb-3 text-600 font-bold uppercase text-xs">Riesgo Residual</h6>
                                    <div className="grid align-items-center">
                                        {/* Lado Izquierdo (1/3): KPI Arriba, Tabla Abajo */}
                                        <div className="col-12 md:col-4 flex flex-column gap-3">
                                            {/* KPI Global */}
                                            <div className="bg-gray-50 p-2 border-round border-1 border-300 flex flex-column align-items-center justify-content-center" style={{ height: '80px' }}>
                                                <span className="text-500 font-bold text-xs mb-1 text-center">{vistaHistorica ? 'Riesgo Global Residual (Histórico)' : 'Riesgo Global Residual'}</span>
                                                {!contextoListo ? <Skeleton width="80%" height="40px" /> : (
                                                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                                        <ApacheEchart chartOpciones={kpiGlobalResidual} isLoading={loading} ref={setChartRef('kpi-global-residual')} />
                                                    </div>
                                                )}
                                            </div>
                                            {/* Tabla */}
                                            {!contextoListo ? <Skeleton width="100%" height="140px" /> : (
                                                <DataTable value={tablaResidual || []} loading={loading} size="small" stripedRows className="border-1 border-300 border-round text-xs">
                                                    <Column field="nivel" header="Nivel" bodyClassName="font-bold"></Column>
                                                    <Column field="cantidad" header={vistaHistorica ? "No. Matrices" : "No. Clientes"} align="right"></Column>
                                                </DataTable>
                                            )}
                                        </div>
                                        {/* Lado Derecho (2/3): Pie Chart */}
                                        <div className="col-12 md:col-8">
                                            {!contextoListo ? <Skeleton shape="circle" size="240px" className="mx-auto" /> : (
                                                <div style={{ height: '260px', width: '100%', position: 'relative' }}>
                                                    <ApacheEchart chartOpciones={pieChartResidual} isLoading={loading} ref={setChartRef('pie-residual')} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* FILA 4: Evolución Temporal */}
                    <div className="surface-ground p-2 border-round">
                        <h6 className="flex align-items-center gap-2 mt-0 mb-2 text-700 text-sm uppercase">
                            <i className="pi pi-chart-line text-blue-600"></i> Gráfico temporal de los Riesgos
                        </h6>
                        <div className="bg-white p-2 border-round shadow-1 flex flex-column" style={{ height: '280px' }}>
                            {!contextoListo ? <Skeleton width="100%" height="100%" /> : (
                                <div style={{ flex: 1, position: 'relative', minHeight: 0, minWidth: 0 }}>
                                    <ApacheEchart chartOpciones={lineChartEvolucion} isLoading={loading} ref={setChartRef('line-evolucion')} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Button loading={loading || loadingPdf} icon="pi pi-file-pdf" onClick={exportarPDF} rounded tooltip="Exportar PDF" tooltipOptions={{ position: 'left' }}
                className={`shadow-6 transition-all transition-duration-300 ${contextoListo ? 'fadein opacity-100 scalein' : 'opacity-0 pointer-events-none scaleout'}`}
                style={{ position: 'fixed', bottom: '53px', right: '30px', zIndex: 999, width: '45px', height: '45px', backgroundColor: '#ef4444', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
            <Dialog header={`Detalle de KPI: ${kpiModal.title}`} visible={kpiModal.visible} className="dialog-personalizado" style={{ width: '85vw' }} breakpoints={{ '960px': '95vw', '640px': '100vw' }} maximizable modal onHide={() => { setKpiModal({ visible: false, type: null, title: '' }); estadoTabla.setData([]); estadoTabla.resetLazyParams(); }}>
                <TablaDinamica modelo={DetalleKpi.model} estadoTabla={estadoTabla} datos={estadoTabla.data || []} totalRegistros={estadoTabla.totalRecords || 0} dataKey="id" cuerpoTemplates={bodyTemplates} acciones={ColumnaAcciones} />
            </Dialog>
        </>
    );
};